import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { aiApi } from "./aiApi";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("ms-MY", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

/** Pulih DATA AI SAHAJA (perbualan, mesej, fail, memori di Atlas) dari backup cloud - data lain tidak disentuh. */
export default function AIAtlasRestore({ onDone }) {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState("");
  const [text, setText] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const load = () => aiApi.atlasBackups().then((d) => setRows(d.backups || [])).catch(() => setRows([]));
  const poll = () => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const s = await aiApi.restoreStatus().catch(() => null);
      if (!s) return;
      setJob(s);
      if (s.status !== "running") {
        clearInterval(timer.current);
        if (s.status === "done") { toast.success(`Data AI dipulihkan: ${s.docs_done} dokumen`); onDone && onDone(); }
        else toast.error(s.error || "Pemulihan gagal");
      }
    }, 3000);
  };
  useEffect(() => { load(); return () => clearInterval(timer.current); }, []);

  const start = async () => {
    try {
      await aiApi.atlasRestore(pick, text);
      setJob({ status: "running", phase: "queued" }); setPick(""); setText(""); poll();
      toast.success("Pemulihan data AI dimulakan (backup keselamatan dibuat dahulu)");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mulakan pemulihan"); }
  };

  const running = job?.status === "running";
  return (
    <div className="rounded-xl bg-white border border-slate-100 p-2.5 space-y-1.5" data-testid="ai-atlas-restore">
      <div className="flex items-center gap-1.5 text-[12px] font-bold text-slate-700"><History className="h-3.5 w-3.5 text-sky-600" /> Pulih Data AI dari Backup</div>
      <div className="text-[10.5px] text-slate-500">Hanya perbualan, mesej, kad tindakan, fail & memori Mimi dipulihkan. Data taska lain TIDAK disentuh. Backup keselamatan dibuat dahulu.</div>
      {job && (
        <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold ${running ? "bg-sky-50 text-sky-700" : job.status === "done" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="ai-atlas-restore-status">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : job.status === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {running ? `Sedang dipulihkan (${job.phase || "..."}) ${job.current_collection || ""} ${job.docs_done ? `· ${job.docs_done} dok` : ""}`
            : job.status === "done" ? `Selesai: ${job.docs_done} dokumen dipulihkan` : (job.error || "Gagal")}
        </div>
      )}
      {!rows ? <div className="h-10 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        : rows.length === 0 ? <div className="text-[11px] text-slate-400" data-testid="ai-atlas-restore-empty">Tiada backup cloud lagi.</div>
          : (
            <div className="max-h-40 overflow-y-auto space-y-1" data-testid="ai-atlas-restore-list">
              {rows.map((b) => (
                <button key={b.file_id} type="button" disabled={!b.ai_docs || running} onClick={() => { setPick(b.file_id); setText(""); }}
                  className={`w-full text-left rounded-lg border px-2 py-1.5 transition-colors ${pick === b.file_id ? "border-sky-400 bg-sky-50" : "border-slate-100 hover:bg-slate-50"} disabled:opacity-50`}
                  data-testid="ai-atlas-restore-item">
                  <div className="text-[11.5px] font-semibold text-slate-700">{fmt(b.uploaded_at)} <span className="font-normal text-slate-400">· {b.size_mb} MB · {b.source}</span></div>
                  <div className="text-[10.5px] text-slate-500">{b.ai_docs ? `Data AI: ${b.ai_docs} dokumen (${Object.keys(b.ai_collections).length} koleksi)` : "Tiada data AI dalam backup ini"}</div>
                </button>
              ))}
            </div>
          )}
      {pick && (
        <div className="space-y-1.5 rounded-lg bg-amber-50 border border-amber-200 p-2" data-testid="ai-atlas-restore-confirm">
          <div className="text-[11px] font-semibold text-amber-800">Data AI semasa akan DIGANTI dengan data dalam backup ini. Taip <b>PULIH</b> untuk sahkan.</div>
          <div className="flex gap-1.5">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="PULIH" className="h-8 text-xs bg-white" data-testid="ai-atlas-restore-text" />
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPick("")} data-testid="ai-atlas-restore-cancel">Batal</Button>
            <Button size="sm" className="h-8 rounded-lg text-xs bg-amber-600 hover:bg-amber-700 text-white" disabled={text.trim().toUpperCase() !== "PULIH"} onClick={start} data-testid="ai-atlas-restore-start">
              <RotateCcw className="h-3.5 w-3.5 mr-1" />Pulih
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
